import type {
    IteroClinic,
    IteroLinkedPractice,
    IteroLiveCheck,
    IteroRefreshResult,
    IteroStatus
} from '../../../../services/iteroConnection';

export type FormatTime = (iso: string) => string;

export interface IteroExpiryText {
    text: string;
    expired: boolean;
}

export interface IteroStatusSummary {
    connected: boolean;
    headline: string;
    expiry: IteroExpiryText | null;
    sharedLine: string | null;
}

export type IteroClinicListView = 'loading' | 'error' | 'empty' | 'list';

const NOT_CONNECTED_TEXT = 'Not connected to iTero';
export const EMPTY_CLINICS_TEXT =
    "No iTero clinics are available to ODO's iTero account. Ask Align to add the ODO Ortho user to the clinic.";

export function practiceNames(practices: IteroLinkedPractice[]): string {
    return practices.map((practice) => practice.name).join(', ');
}

function isTokenExpired(expiresAt: string | null, now: number): boolean {
    if (expiresAt === null) return false;
    const time = new Date(expiresAt).getTime();
    return !Number.isNaN(time) && time < now;
}

function describeExpiry(
    expiresAt: string | null,
    now: number,
    formatTime: FormatTime
): IteroExpiryText | null {
    if (expiresAt === null) return null;
    const time = formatTime(expiresAt);
    if (isTokenExpired(expiresAt, now)) {
        return { text: time ? `Token expired ${time}` : 'Token expired', expired: true };
    }
    return { text: time ? `token expires ${time}` : 'token stored', expired: false };
}

export function describeIteroStatus(
    status: IteroStatus,
    now: number,
    formatTime: FormatTime
): IteroStatusSummary {
    const sharedLine =
        status.sharedWith.length > 0 ? `Also used by ${practiceNames(status.sharedWith)}` : null;
    if (!status.connected) {
        return { connected: false, headline: NOT_CONNECTED_TEXT, expiry: null, sharedLine };
    }
    return {
        connected: true,
        headline: `Connected to iTero clinic ${status.clinicId ?? 'unknown'}`,
        expiry: describeExpiry(status.accessTokenExpiresAt, now, formatTime),
        sharedLine
    };
}

function doctorCount(doctors: number): string {
    return `${doctors} ${doctors === 1 ? 'doctor' : 'doctors'}`;
}

export function describeTestResult(live: IteroLiveCheck | undefined): {
    text: string;
    ok: boolean;
} {
    if (!live) return { text: 'Test failed: iTero returned no result', ok: false };
    if (!live.ok) {
        return { text: live.reason ? `Test failed: ${live.reason}` : 'Test failed', ok: false };
    }
    const parts = [
        live.companyName,
        live.doctors === undefined ? undefined : doctorCount(live.doctors)
    ];
    const text = parts.filter((part): part is string => Boolean(part)).join(' · ');
    return { text: text || 'Connection OK', ok: true };
}

export function refreshToastMessage(result: IteroRefreshResult, formatTime: FormatTime): string {
    if (!result.refreshed && result.reason === 'not_due') {
        const expiresAt = result.status.accessTokenExpiresAt;
        const time = expiresAt ? formatTime(expiresAt) : '';
        return time ? `Token is still fresh (expires ${time})` : 'Token is still fresh';
    }
    if (result.repaired) return 'Reconnected to iTero';
    return 'Token refreshed';
}

export function disconnectConfirmMessage(status: IteroStatus): string {
    const clinicId = status.clinicId ?? 'unknown';
    if (status.sharedWith.length === 0) {
        return `Disconnect this practice from iTero? This also removes ODO's connection to iTero clinic ${clinicId} for scans and patient search.`;
    }
    return `Disconnect this practice from iTero clinic ${clinicId}? ${practiceNames(status.sharedWith)} stay connected.`;
}

export const DISCONNECT_UNKNOWN_STATUS_MESSAGE =
    "Disconnect this practice from iTero? ODO will stop reaching the practice's iTero clinic for scans and patient search.";

export const STATUS_NEEDS_VIEW_ACCESS =
    'You can manage the iTero connection but not view its status.';

export function connectedToast(clinicId: number): string {
    return `Connected to iTero clinic ${clinicId}`;
}

export function currentClinicId(clinics: IteroClinic[]): number | null {
    return clinics.find((clinic) => clinic.isCurrent)?.clinicId ?? null;
}

export function canConnectTo(clinics: IteroClinic[], selectedId: number | null): boolean {
    if (selectedId === null) return false;
    const selected = clinics.find((clinic) => clinic.clinicId === selectedId);
    return selected !== undefined && !selected.isCurrent;
}

// linkedPractices can include this practice itself; only the others share the connection.
export function sharingNote(clinic: IteroClinic | undefined, practiceId: string): string | null {
    if (!clinic || clinic.isCurrent) return null;
    const others = clinic.linkedPractices.filter((practice) => practice.practiceId !== practiceId);
    if (others.length === 0) return null;
    return `This practice will share the iTero connection with ${practiceNames(others)}.`;
}

export function clinicListView(query: {
    isLoading: boolean;
    isError: boolean;
    clinics: IteroClinic[] | undefined;
}): IteroClinicListView {
    if (query.isLoading) return 'loading';
    if (query.isError) return 'error';
    if (!query.clinics || query.clinics.length === 0) return 'empty';
    return 'list';
}
